import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductCategory } from '@/mocks/productCategories';

interface CategorySidebarProps {
  categories: ProductCategory[];
  activeCategoryCode: string | null;
  onCategorySelect: (code: string | null) => void;
  totalCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function CategorySidebar({
  categories,
  activeCategoryCode,
  onCategorySelect,
  totalCount,
  collapsed,
  onToggleCollapse,
}: CategorySidebarProps) {
  const { t } = useTranslation('products');

  // Initialize with ALL primary categories expanded so the full tree is visible
  const allPrimaryCodes = useMemo(
    () => new Set(categories.map((c) => c.code)),
    [categories],
  );

  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(() => allPrimaryCodes);

  // Keep newly loaded categories expanded
  useEffect(() => {
    setExpandedCodes(allPrimaryCodes);
  }, [allPrimaryCodes]);

  // Ensure active category's primary stays expanded
  useEffect(() => {
    if (!activeCategoryCode) return;
    for (const primary of categories) {
      // Active is either this primary itself or one of its children
      if (
        primary.code === activeCategoryCode ||
        primary.children.some((c) => c.code === activeCategoryCode)
      ) {
        setExpandedCodes((prev) => {
          if (prev.has(primary.code)) return prev;
          const next = new Set(prev);
          next.add(primary.code);
          return next;
        });
        break;
      }
    }
  }, [activeCategoryCode, categories]);

  const toggleExpand = (code: string) => {
    setExpandedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  // Collapsed state — show narrow strip with expand button
  if (collapsed) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="flex flex-col items-center py-3 gap-3">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-50 transition-colors text-ink-400 cursor-pointer"
            aria-label="Expand sidebar"
            title={t('products.sidebar.expand') || 'Expand sidebar'}
          >
            <i className="ri-menu-fold-line text-base" />
          </button>
          <button
            type="button"
            onClick={() => onCategorySelect(null)}
            className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-semibold transition-colors cursor-pointer ${
              activeCategoryCode === null
                ? 'bg-warm-100 text-warm-600'
                : 'bg-cream-50 text-ink-500 hover:bg-cream-100'
            }`}
            title={`${t('products.category.all')} (${totalCount})`}
          >
            {totalCount}
          </button>
          {categories.slice(0, 6).map((primary) => (
            <button
              key={primary.code}
              type="button"
              onClick={() => onCategorySelect(primary.code)}
              className={`w-8 h-8 flex items-center justify-center rounded-md text-[10px] font-semibold transition-colors cursor-pointer truncate ${
                activeCategoryCode === primary.code ||
                primary.children.some((c) => c.code === activeCategoryCode)
                  ? 'bg-warm-100 text-warm-600'
                  : 'bg-cream-50 text-ink-400 hover:bg-cream-100'
              }`}
              title={t(primary.labelKey)}
            >
              {t(primary.labelKey).charAt(0)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-ink-100 bg-white flex items-center justify-between">
        <h3 className="text-sm font-bold text-ink-700 uppercase tracking-wide">
          {t('products.sidebar.title')}
        </h3>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-cream-50 transition-colors text-ink-400 cursor-pointer"
          aria-label="Collapse sidebar"
          title={t('products.sidebar.collapse') || 'Collapse sidebar'}
        >
          <i className="ri-menu-fold-line text-base" />
        </button>
      </div>
      <ul className="py-1">
        {/* All */}
        <li>
          <button
            type="button"
            onClick={() => onCategorySelect(null)}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
              activeCategoryCode === null
                ? 'bg-warm-50 text-warm-600 font-semibold border-l-2 border-warm-500'
                : 'text-ink-600 hover:bg-cream-50 border-l-2 border-transparent'
            }`}
          >
            <span>{t('products.category.all')}</span>
            <span className={`text-xs ${activeCategoryCode === null ? 'text-warm-400' : 'text-ink-300'}`}>
              ({totalCount})
            </span>
          </button>
        </li>

        {categories.map((primary) => {
          const isExpanded = expandedCodes.has(primary.code);
          const isActive = activeCategoryCode === primary.code;

          return (
            <li key={primary.code}>
              {/* Primary Category Header */}
              <div
                className={`flex items-stretch border-l-2 transition-colors ${
                  isActive
                    ? 'bg-warm-50 border-warm-500'
                    : 'border-transparent'
                }`}
              >
                {/* Arrow toggle — expand/collapse only, no filter */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(primary.code);
                  }}
                  className="flex items-center justify-center w-9 shrink-0 text-ink-400 hover:bg-cream-50 transition-colors cursor-pointer"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {isExpanded ? (
                      <i className="ri-arrow-down-s-line text-xs" />
                    ) : (
                      <i className="ri-arrow-right-s-line text-xs" />
                    )}
                  </span>
                </button>

                {/* Name — filter by L1 category */}
                <button
                  type="button"
                  onClick={() => {
                    if (isActive) {
                      // Already active — go back to All
                      onCategorySelect(null);
                    } else {
                      onCategorySelect(primary.code);
                      // Ensure expanded
                      if (!isExpanded) {
                        toggleExpand(primary.code);
                      }
                    }
                  }}
                  className={`flex-1 flex items-center px-2 py-2.5 text-left text-sm transition-colors cursor-pointer ${
                    isActive
                      ? 'text-warm-600 font-semibold'
                      : 'text-ink-700 hover:bg-cream-50'
                  }`}
                >
                  <span className="font-semibold truncate">{t(primary.labelKey)}</span>
                </button>
              </div>

              {/* Secondary Categories */}
              {isExpanded && (
                <ul className="py-0.5">
                  {primary.children.map((secondary) => {
                    const isActive = activeCategoryCode === secondary.code;

                    return (
                      <li key={secondary.code}>
                        <button
                          type="button"
                          onClick={() => onCategorySelect(secondary.code)}
                          className={`w-full flex items-center justify-between pl-10 pr-4 py-2 text-left text-sm transition-colors border-l-2 cursor-pointer ${
                            isActive
                              ? 'bg-warm-50 text-warm-600 font-semibold border-warm-500'
                              : 'text-ink-500 hover:bg-cream-50 border-transparent'
                          }`}
                        >
                          <span className="truncate">{t(secondary.labelKey)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}